import { useEffect } from "react";

// What search engines and browser tabs show for each page: its title, its description (the
// grey text under the title in Google), and its canonical address, the one version of the page's
// address Google should list (so /shop and /Shop don't count as two pages).
//
// Fixed pages get theirs from ROUTE_META below, applied on every page change (RouteMeta in
// RoutesBlog.jsx). Product and recipe post pages call usePageMeta once their content has loaded.
// Google reads these after the page's JavaScript runs. Link previews on social media and in
// messages read only public/index.html, so they show the site's defaults set there.

export const SITE_NAME = "am / human";
// The address Google should list pages under. Set REACT_APP_SITE_URL to change it.
const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://amhuman.ca").replace(/\/$/, "");

// Used for the home page and any page without its own. Keep in step with public/index.html.
export const DEFAULT_TITLE = "am / human | Cold Process Soap & Botanical Body Care";
export const DEFAULT_DESCRIPTION =
  "Handcrafted cold process soap and botanical body care, thoughtfully formulated for skin and senses. Made in small batches in Canada.";

// Google shows about 155 characters of a description; longer ones are cut off mid-word.
const DESCRIPTION_MAX = 155;

// Fixed pages, by their address in lower case. `path` is the address Google should list.
const ROUTE_META = {
  "/": { path: "/" },
  "/shop": {
    title: "Shop",
    path: "/Shop",
    description:
      "Shop handcrafted cold process soaps and botanical body care from am / human. Thoughtfully formulated and made in small batches in Canada.",
  },
  "/recipes": {
    title: "Recipes",
    path: "/Recipes",
    description:
      "Soap and skincare recipes from am / human: formulations for cold process soap, face creams and body care, with ingredients and notes.",
  },
  "/soaps": {
    title: "Soap Recipes",
    path: "/Soaps",
    description:
      "Cold process soap recipes from am / human, with ingredients, formulation details and how each bar is made.",
  },
  "/facecream": {
    title: "Face Cream Recipes",
    path: "/FaceCream",
    description: "Face cream recipes from am / human: formulations, ingredients and how each cream is made.",
  },
  "/bodywash": {
    title: "Body Care Recipes",
    path: "/BodyWash",
    description: "Body care recipes from am / human, from body washes to butters, with ingredients and formulation notes.",
  },
  "/about": {
    title: "About",
    path: "/About",
    description:
      "The story behind am / human: handcrafted cold process soap and botanical body care, thoughtfully formulated and made in Canada.",
  },
  "/contact": {
    title: "Contact",
    path: "/Contact",
    description: "Get in touch with am / human about an order, our products or anything else.",
  },
  "/privacy": {
    title: "Privacy Policy",
    path: "/Privacy",
    description: "How am / human collects, uses and protects your personal information.",
  },
  "/terms": {
    title: "Terms",
    path: "/Terms",
    description: "The terms for using the am / human website and shopping with us.",
  },
};

// Pages for one person (accounts, checkout, admin tools) that shouldn't appear in search results.
const PRIVATE_PAGES = {
  "/login": "Log in",
  "/signup": "Create an account",
  "/forgot-password": "Forgot password",
  "/reset-password": "Choose a new password",
  "/settings": "Settings",
  "/orders": "Your orders",
  "/checkout": "Checkout",
  "/payment-success": "Order confirmed",
  "/write": "Write a post",
  "/editpost": "Edit posts",
  "/editshop": "Edit shop",
};

// The meta for an address, before any page content has loaded.
export const metaForPath = (pathname) => {
  const key = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  if (PRIVATE_PAGES[key]) return { title: PRIVATE_PAGES[key], noindex: true };
  if (ROUTE_META[key]) return ROUTE_META[key];
  // Product and recipe post pages fill in their own once loaded (usePageMeta).
  if (key.startsWith("/dynamicshop/")) return { title: "Shop" };
  if (key === "/dynamicposts") return { title: "Recipes" };
  return {};
};

const setMetaTag = (name, content) => {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (content == null) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setCanonical = (path) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!path) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", SITE_URL + path);
};

// Sets the page's title, description, canonical address and, for private pages, "noindex"
// (asks search engines to leave the page out of their results).
export const applyPageMeta = ({ title, description, path, noindex = false } = {}) => {
  document.title = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  setMetaTag("description", description || DEFAULT_DESCRIPTION);
  setMetaTag("robots", noindex ? "noindex" : null);
  setCanonical(noindex ? null : path);
};

// A description from a page's own text, e.g. a product's tagline and description, shortened at
// a word to what Google shows.
export const toDescription = (...parts) => {
  const text = parts
    .filter((part) => typeof part === "string" && part.trim())
    .map((part) => part.trim().replace(/\s+/g, " "))
    .join(" — ");
  if (text.length <= DESCRIPTION_MAX) return text;
  const cut = text.slice(0, DESCRIPTION_MAX - 1);
  return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[\s,.;:—-]+$/, "")}…`;
};

// For pages whose title and description come from their content. Pass null until it has loaded.
export const usePageMeta = (meta) => {
  const { title, description, path, noindex } = meta ?? {};
  useEffect(() => {
    if (meta) applyPageMeta({ title, description, path, noindex });
    // `meta` is a new object on every render; its fields are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(meta), title, description, path, noindex]);
};
